/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { FormEvent, FunctionComponent, useEffect } from "react";
import Grid from "@mui/material/Grid";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import PoaAgreementText from "@/src/components/Form/Common/PowerOfAttorney/PoaAgreementText";
import { useForm } from "@/src/provider/Form/FormProvider";
import { Box } from "@mui/material";
import {
  getPowerOfAttorneyAuthorizationItems,
  getPowerOfAttorneyAwarenessText,
  getPowerOfAttorneyPreamble
} from "@/src/services/contractLaw/powerOfAttorneyInformationHelper";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import MultipleSignatures from "@/src/components/Form/Common/Signature/MultipleSignatures";
import PowerOfAttorneySignatoryHighlightBox from "./PowerOfAttorneySignatoryHighlightBox";
import PowerOfAttorneyOutroHighlightBox from "./PowerOfAttorneyOutroHighlightBox";
import dayjs from "dayjs";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

type PowerOfAttorneyProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
  SignatoryHighlightBox?: FunctionComponent<{ product: AbstractCase }> | null;
  OutroHighlightBox?: FunctionComponent<{ product: AbstractCase }> | null;
};

export default function PowerOfAttorney({
  product,
  submitProps,
  SignatoryHighlightBox = PowerOfAttorneySignatoryHighlightBox,
  OutroHighlightBox = PowerOfAttorneyOutroHighlightBox,
}: PowerOfAttorneyProps) {
  const { handleSubmit, handleChange, values } = useForm();

  if (!product.signatories || !product.additionalClients) {
    return null;
  }

  const { powerOfAttorney } = values;
  useEffect(() => {
    if (powerOfAttorney === null) {
      handleChange({ target: { name: "powerOfAttorney", value: dayjs().format("DD.MM.YYYY") } });
    }
  }, [powerOfAttorney]);

  const handlePoaSubmit = async (event: FormEvent<HTMLFormElement>) => {
    if (!submitProps.isMinimal) {
      await handleSubmit(event);
      return;
    }
    await handleSubmit(event, { preventRedirect: true });
  };

  const usePlural = product.additionalClients?.length > 0 || product.clientLegalRepresentativeAmount === 2;

  return (
    <ContentBox headline={<strong>Vollmacht</strong>} headlineVariant={"h5"} showDivider={true}>
      <Box component={"form"} id={"application-form"} onSubmit={handlePoaSubmit}>
        <div>
          {getPowerOfAttorneyPreamble(product, usePlural)}
          <ul>
            {getPowerOfAttorneyAuthorizationItems(product).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {getPowerOfAttorneyAwarenessText(product, usePlural)}
          <PoaAgreementText lawFirmName={product.lawFirm.name} multipleClients={usePlural} />
        </div>
        {!!SignatoryHighlightBox && <SignatoryHighlightBox product={product} />}
        <MultipleSignatures
          product={product}
          signatories={product.signatories ? product.signatories.sort((a, b) => (a.id || 0) - (b.id || 0)) : [product.client]}
        />

        <Grid container>
          <Grid size={12}>
            <ButtonLoading
              color="primary"
              variant="contained"
              type="submit"
              fullWidth={true}
              disabled={product.signatories.some((signee) => !signee.signature)}
              isLoading={submitProps.isLoading}
            >
              Vollmacht erteilen und Fall prüfen lassen
            </ButtonLoading>
          </Grid>
        </Grid>
        {!!OutroHighlightBox && (
          <Box sx={{ paddingTop: 3 }}>
            <OutroHighlightBox product={product} />
          </Box>
        )}
      </Box>
    </ContentBox>
  );
}
